/**
 * useClickOutside Hook
 *
 * A reusable hook for detecting clicks outside a referenced element.
 * Commonly used for closing modals, dropdowns, and menus when users click elsewhere.
 * @module use-click-outside
 *
 * Features:
 * - Uses mousedown event for faster response than click
 * - Properly cleans up event listeners
 * - TypeScript generic for type safety
 * - Only attaches listener when enabled
 *
 * Architecture Decision:
 * Using mousedown instead of click provides better UX - the menu closes
 * before the click event propagates, making the interaction feel more responsive.
 *
 * @example
 * ```tsx
 * function Dropdown() {
 *   const [open, setOpen] = useState(false);
 *   const ref = useRef<HTMLDivElement>(null);
 *
 *   useClickOutside(ref, () => setOpen(false), open);
 *
 *   return (
 *     <div ref={ref}>
 *       {open && <DropdownMenu />}
 *     </div>
 *   );
 * }
 * ```
 */
import { RefObject, useEffect } from 'react';

/**
 * Hook to detect clicks outside a referenced element
 * @param ref - React ref object pointing to the element to monitor
 * @param callback - Function to call when click occurs outside the element
 * @param enabled - Whether the listener is active (default: true)
 *
 * @example
 * ```tsx
 * const menuRef = useRef<HTMLDivElement>(null);
 * useClickOutside(menuRef, () => setMenuOpen(false), isMenuOpen);
 * ```
 */
export function useClickOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T>,
  callback: () => void,
  enabled: boolean = true,
): void {
  useEffect(() => {
    /**
     * Handle mousedown events
     * Uses mousedown for faster response - fires before click completes
     * @param event
     */
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    };

    // Only attach listener when enabled (e.g., when menu is open)
    if (enabled) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup on unmount or when enabled changes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, callback, enabled]);
}
